import { existsSync, rmSync, lstatSync, unlinkSync } from "node:fs";
import { join } from "node:path";
import readline from "node:readline";

interface CleanOptions {
  cwd: string;
  yes?: boolean;
  dryRun?: boolean;
  purgeAgents?: boolean;
}

function askConfirmation(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
  });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim().toLowerCase());
    });
  });
}

function isSymlink(filePath: string): boolean {
  try {
    return lstatSync(filePath).isSymbolicLink();
  } catch (_) {
    return false;
  }
}

export async function runClean({ cwd, yes = false, dryRun = false, purgeAgents = false }: CleanOptions): Promise<void> {
  const targets = [
    ".ai-workflow",
    "opencode.jsonc",
    ".workflow-state.json",
    "opencode"
  ];

  const hasAgents = existsSync(join(cwd, ".agents"));

  // Check what actually exists
  const existingTargets = targets
    .map(name => join(cwd, name))
    .filter(path => existsSync(path) || isSymlink(path));

  if (existingTargets.length === 0 && (!hasAgents || !purgeAgents)) {
    console.log("No AI Workflow configuration or files found in the current project.");
    return;
  }

  console.log("\n[AI WORKFLOW] De-initialization");
  console.log("===============================");

  if (dryRun) {
    console.log("[DRY RUN] The following items would be deleted:");
    existingTargets.forEach(path => console.log(`  - ${path}`));
    if (hasAgents && purgeAgents) {
      console.log(`  - ${join(cwd, ".agents")} (customizations folder)`);
    } else if (hasAgents) {
      console.log(`  - NOTE: ${join(cwd, ".agents")} exists but will NOT be deleted (run with --purge-agents to delete)`);
    }
    return;
  }

  // Interactive prompts if --yes / -y is not passed
  if (!yes) {
    if (!process.stdin.isTTY) {
      console.log("Error: Non-interactive terminal detected. Run with --yes to bypass confirmation.");
      if (process.env.NODE_ENV === "test") {
        // Allow tests to bypass TTY block if they mock/stub process.stdin
      } else {
        process.exit(1);
      }
    }

    const confirmDeinit = await askConfirmation("Are you sure you want to de-initialize AI Workflow from the project? [y/N] ");
    if (confirmDeinit !== "y" && confirmDeinit !== "yes") {
      console.log("Aborted.");
      return;
    }

    if (hasAgents && !purgeAgents) {
      const confirmPurge = await askConfirmation("A custom '.agents' folder was detected. Do you want to purge it (this deletes custom prompts/rules/skills)? [y/N] ");
      if (confirmPurge === "y" || confirmPurge === "yes") {
        purgeAgents = true;
      }
    }
  }

  // Add .agents if requested and exists
  if (purgeAgents && hasAgents) {
    existingTargets.push(join(cwd, ".agents"));
  }

  console.log("\nRemoving files...");
  for (const target of existingTargets) {
    try {
      if (isSymlink(target)) {
        unlinkSync(target);
        console.log(`Deleted symlink: ${target}`);
      } else {
        rmSync(target, { recursive: true, force: true });
        console.log(`Deleted directory/file: ${target}`);
      }
    } catch (error: any) {
      console.error(`Failed to delete ${target}: ${error.message}`);
    }
  }

  console.log("\nAI Workflow has been successfully removed from this project.");
}
